import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';

/**
 * Terms of Service — placeholder copy. BEFORE PUBLIC LAUNCH: replace every
 * paragraph under `<article>` with legally-binding text from Termly.io,
 * Iubenda, or an attorney. Update EFFECTIVE_DATE, CONTACT_EMAIL, and
 * JURISDICTION as well.
 */
const EFFECTIVE_DATE = 'February 1, 2026';
const CONTACT_EMAIL = 'support@thehorseshowsmaster.com';
const JURISDICTION = 'the State of [YOUR STATE], United States';

export default function TermsOfService() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5] py-10 px-4" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#9E9E9E] hover:text-white text-sm mb-6 transition-colors"
          data-testid="terms-back-btn"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>

        <h1 className="text-3xl sm:text-4xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>
          Terms of Service
        </h1>
        <p className="text-sm text-[#9E9E9E] mb-8">Effective {EFFECTIVE_DATE}</p>

        <article className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-bold text-white mb-2">1. Acceptance of Terms</h2>
            <p>
              By accessing or using Show Master ("Service"), you agree to be bound by these Terms of Service.
              If you do not agree, do not use the Service. We may update these Terms at any time; continued use
              after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">2. Accounts</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for
              all activity on your account. You must be at least 18 years old (or have a parent/guardian's
              consent) to use this Service.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">3. Producer Responsibilities</h2>
            <p>
              Organizations and producers using Show Master to list, manage, or collect payments for
              equestrian events are solely responsible for: (a) the accuracy of show information;
              (b) honoring entries; (c) paying out winnings; (d) complying with applicable laws,
              insurance requirements, and local regulations; (e) refund policies communicated to riders.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">4. Rider Responsibilities</h2>
            <p>
              Riders are responsible for reviewing show details, rules, and refund policies before entering.
              Payment is processed via our third-party payment provider (Square) and is subject to their terms.
              Show Master does not guarantee the delivery of any event.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">5. Payments, Fees, and Refunds</h2>
            <p>
              Show Master charges a service fee on entries processed through the Service. All fees are
              disclosed at checkout. Refunds are handled by the producer of the individual show. Show Master
              does not issue refunds on behalf of producers unless required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">6. Assumption of Risk</h2>
            <p>
              Equestrian activities are inherently dangerous. By entering any show listed through the Service,
              riders acknowledge the risks associated with horses and agree that Show Master is not responsible
              for injuries, property damage, or losses occurring at a show.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">7. Prohibited Uses</h2>
            <p>
              You may not use the Service to: (a) violate any law; (b) infringe intellectual property;
              (c) upload malicious code; (d) scrape or harvest data; (e) impersonate others; (f) list
              fraudulent shows or collect payments under false pretenses.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. Show Master
              disclaims all warranties including merchantability, fitness for a particular purpose, and
              non-infringement, to the maximum extent permitted by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, Show Master's total liability to you for any claim
              arising out of the Service is limited to the amount you paid Show Master in the 12 months
              preceding the claim. Show Master is not liable for indirect, incidental, or consequential damages.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">10. Termination</h2>
            <p>
              We may suspend or terminate your account at any time for violation of these Terms or if
              required by law. You may terminate your account at any time by contacting support.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">11. Governing Law</h2>
            <p>
              These Terms are governed by the laws of {JURISDICTION}, without regard to conflict-of-laws
              principles. Any disputes shall be resolved in the courts located in that jurisdiction.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold text-white mb-2">12. Contact</h2>
            <p>
              Questions about these Terms? Contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#0096FF] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>

          <section className="bg-[#1a1a1a] border border-[#FF9500]/30 rounded-md p-4 mt-8">
            <p className="text-[11px] text-[#FF9500] font-semibold uppercase tracking-wide mb-1">
              Placeholder Notice
            </p>
            <p className="text-[11px] text-[#9E9E9E]">
              This document is placeholder content. Before public launch, replace with legally-binding
              text from Termly.io, Iubenda, or a licensed attorney. Update the effective date, contact
              email, and jurisdiction above.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
}
